/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import {
  FoodmaxapipbMenuV3ItemFilter,
  FoodmaxapipbEntityType,
} from "generated/foodmaxapi.type"

export enum HttpMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
}

// Base type for merchant context
export interface MerchantContext {
  merchantID?: string
  merchantGroupID?: string
}

// Request types for Menu V3 API
export interface MenuV3ModifierGroupsRequest extends MerchantContext {
  limit?: number
  pageIndex?: number
}

export interface MenuV3HierarchiesRequest extends MerchantContext {
  limit?: number
  pageIndex?: number
  parentType?: FoodmaxapipbEntityType
  parentID?: string
}

export interface MenuV3ItemsRequest extends MerchantContext {
  limit?: number
  pageIndex?: number
  parentType?: FoodmaxapipbEntityType
  parentID?: string
  filter?: Partial<FoodmaxapipbMenuV3ItemFilter>
}

export type MenuV3EditorStatusRequest = MerchantContext

// Request type for Menu V2 API (example)
export type MenuV2Request = MerchantContext

// Request type for Selling Times API
export interface ListSellingTimesRequest extends MerchantContext {
  isActive?: boolean
  page?: number
  pageSize?: number
  name?: string
}

// Request type for fetching modifier groups by IDs
export interface GetModifierGroupsByIDsRequest extends MerchantContext {
  modifierGroupIDs: string[]
}

// Request type for fetching items by IDs (GetItemDetailsByItemIDs endpoint)
export interface GetItemDetailsByItemIDsRequest extends MerchantContext {
  itemIDs: string[]
  needStock?: boolean
  needModifierGroups?: boolean
}

// Request type for search items (SearchItems endpoint)
export interface SearchItemsRequest extends MerchantContext {
  searchKey: string
  needClassItemsInfo?: boolean
  needCategoryInfo?: boolean
  needStock?: boolean
}

// Request type for search menus for mex (SearchMenusForMex endpoint)
export interface SearchMenusForMexRequest extends MerchantContext {
  keyword?: string
  entityType?: string
  departmentID?: string
}

// Request type for MartItemMenuDisplayV2 endpoint
export interface MartItemMenuDisplayV2Request extends MerchantContext {
  hideModifierGroup?: boolean
}

// Request type for the upload-file endpoint (POST /v2/upload-file)
export interface UploadFileRequest {
  merchantID: string
  file: {
    data: string // base64-encoded image data (without the data:mime;base64, prefix)
    type: string // mime sub-type, e.g. "jpeg", "png"
  }
  category: string // e.g. "menu_item_img"
}

// Response type for the upload-file endpoint
export interface UploadFileResponse {
  url: string
  metadata: {
    detection: {
      errorItems: string[] | null
      warningItems: string[] | null
    }
  }
}

// Request type for ListAttributes endpoint
export interface ListAttributesRequest extends MerchantContext {
  attributeClusterIDs: string[]
  page: number
  name?: string
  locale?: string
}

// Request type for FoodMaxAPI_UpdateItemsAvailableV1 (PUT /v1/items/available-status)
export interface UpdateItemsAvailableV1Request extends MerchantContext {
  availableStatus: number
  itemIDs?: string[]
  departmentIDs?: string[]
  subDepartmentIDs?: string[]
  excludedSubDepartmentIDs?: string[]
  excludedItemIDs?: string[]
}

// Request type for FoodMaxAPI_UpdateItemsSellingTimeV2 (PUT /v2/items/sellingTime)
export interface UpdateItemsSellingTimeRequest extends MerchantContext {
  itemIDs?: string[]
  departmentIDs?: string[]
  subDepartmentIDs?: string[]
  excludedSubDepartmentIDs?: string[]
  excludedItemIDs?: string[]
  sellingTimeID: string
}

// Request type for FoodMaxAPI_DeleteItemV2 (DELETE /v2/items/bulk)
export interface DeleteItemV2Request extends MerchantContext {
  itemIDs?: string[]
  categoryIDs?: string[]
  departmentIDs?: string[]
  subDepartmentIDs?: string[]
  excludedSubDepartmentIDs?: string[]
  excludedItemIDs?: string[]
}

// Request type for fetchIncidentList (GET /v1/merchant/incidentlist)
export interface FetchIncidentListRequest extends MerchantContext {
  merchantID: string // Override to make required (old Redux action expects it)
  incidentType?: string[]
  resolutionType?: string[]
  fromTime?: string
  endTime?: string
  page?: number
  pageSize?: number
}

// Request type for FoodMaxAPI_FetchIncidentStat (GET /v1/merchant/incidentstats)
export interface FetchIncidentStatRequest extends MerchantContext {
  filterBy?: string
}

// Request type for SearchOutlets endpoint (delvplatformapi)
export interface SearchOutletsRequest extends MerchantContext {
  search?: string
  offset?: number
  limit?: number
  includeItemsWithoutPhotosCount?: boolean
  city?: string[]
  asc?: boolean
  modelType?: string
  deliverOption?: string[]
  businessType?: number[]
  includeMenuGroupV2ID?: boolean
}
